import { useEffect, useState, Suspense } from 'react';
import { db } from '../../lib/firebase';
import { doc, onSnapshot } from 'firebase/firestore';
import { useTexture } from '@react-three/drei';
import * as THREE from 'three';

interface OverlayLogos3DProps {
  sideX: number;
  topY: number;
  wallZ: number;
  startX: number;
  width: number;
}

function LogoMesh({ url, position, rotation, scale = 100 }: { url: string; position: [number, number, number]; rotation: [number, number, number]; scale?: number }) {
  const texture = useTexture(url);
  // Optional: calculate aspect ratio
  const aspect = texture.image ? texture.image.width / texture.image.height : 1;

  return (
    <mesh position={position} rotation={rotation}>
      <planeGeometry args={[scale * aspect, scale]} />
      <meshBasicMaterial 
        map={texture} 
        transparent={true} 
        alphaTest={0.1} 
        depthWrite={false} 
        side={THREE.DoubleSide}
      />
    </mesh>
  );
}

export function OverlayLogos3D({ sideX, topY, wallZ, startX, width }: OverlayLogos3DProps) {
  const [leftUrl, setLeftUrl] = useState<string | null>(null);
  const [backUrl, setBackUrl] = useState<string | null>(null);
  const [leftScale, setLeftScale] = useState<number>(380);
  const [leftOffsetY, setLeftOffsetY] = useState<number>(250);
  const [leftOffsetX, setLeftOffsetX] = useState<number>(0);
  const [leftOffsetZ, setLeftOffsetZ] = useState<number>(0);
  const [leftRotationY, setLeftRotationY] = useState<number>(90); // 90° = Math.PI/2 (flush against side wall)
  const [backScale, setBackScale] = useState<number>(380);
  const [backOffsetY, setBackOffsetY] = useState<number>(250);
  const [backOffsetX, setBackOffsetX] = useState<number>(0);
  const [backOffsetZ, setBackOffsetZ] = useState<number>(0);
  const [backRotationY, setBackRotationY] = useState<number>(0);

  useEffect(() => {
    const docRef = doc(db, 'config', 'terminal_assets');
    const unsubscribe = onSnapshot(docRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        setLeftUrl(data.leftWallLogoUrl || null);
        setBackUrl(data.backWallLogoUrl || null);
        if (typeof data.leftScale === 'number') setLeftScale(data.leftScale);
        if (typeof data.leftOffsetY === 'number') setLeftOffsetY(data.leftOffsetY);
        if (typeof data.leftOffsetX === 'number') setLeftOffsetX(data.leftOffsetX);
        if (typeof data.leftOffsetZ === 'number') setLeftOffsetZ(data.leftOffsetZ);
        if (typeof data.leftRotationY === 'number') setLeftRotationY(data.leftRotationY);
        if (typeof data.backScale === 'number') setBackScale(data.backScale);
        if (typeof data.backOffsetY === 'number') setBackOffsetY(data.backOffsetY);
        if (typeof data.backOffsetX === 'number') setBackOffsetX(data.backOffsetX);
        if (typeof data.backOffsetZ === 'number') setBackOffsetZ(data.backOffsetZ);
        if (typeof data.backRotationY === 'number') setBackRotationY(data.backRotationY);
      } else {
        setLeftUrl(null);
        setBackUrl(null);
      }
    });

    return () => unsubscribe();
  }, []);

  return (
    <>
      {leftUrl && (
        <Suspense fallback={null}>
          <LogoMesh 
            url={leftUrl} 
            position={[sideX + leftOffsetX, topY + leftOffsetY, leftOffsetZ]} 
            rotation={[0, (leftRotationY * Math.PI) / 180, 0]} 
            scale={leftScale} 
          />
        </Suspense>
      )}
      {backUrl && (
        <Suspense fallback={null}>
          <LogoMesh 
            url={backUrl} 
            position={[startX + width * 0.75 + backOffsetX, topY + backOffsetY, wallZ + backOffsetZ]} 
            rotation={[0, (backRotationY * Math.PI) / 180, 0]} 
            scale={backScale} 
          />
        </Suspense>
      )}
    </>
  );
}
